import {
  Location,
  Editor,
  Node,
  Path,
  Range,
  Transforms,
} from 'domains/reporter/RichTextEditor/core';
import { LIST_PLUGIN_ID } from './types';
import { PARAGRAPH_PLUGIN_ID, PARAGRAPH_LIST_ITEM_VARIANT } from '../paragraph/types';
import {
  BULLETED_LIST_HOTKEY_MAC,
  BULLETED_LIST_HOTKEY_WINDOWS,
  DECREASE_INDENT_HOTKEY_MAC,
  DECREASE_INDENT_HOTKEY_WINDOWS,
  INCREASE_INDENT_HOTKEY_MAC,
  INCREASE_INDENT_HOTKEY_WINDOWS,
  LIST_VARIANTS,
  LIST_WRAPPER,
  NUMBERED_LIST_HOTKEY_MAC,
  NUMBERED_LIST_HOTKEY_WINDOWS,
} from './constants';
import type { ParagraphPluginElement } from '../paragraph/types';
import type { ListVariant } from './constants';
import { createInlineBookmark } from '../inlineBookmark/utils';
import { getPathRefSafe, isApple } from '../../utils';
import { INLINE_BOOKMARK_PLUGIN_ID } from '../inlineBookmark/types';
import { BRACKET_TYPES } from '../../constants';
import type { CurrentActiveListVariantState } from './hooks/useCurrentList';
import { logger } from 'modules/logger';
import { prettifyHotkey } from 'utils/prettifyHotkey';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { demoteListItems } from './functions';
import { Element } from 'slate';
import { CustomElement, ListPluginElement } from '../../slate-custom-types';

export const isListChildValid = (node: Node): boolean => {
  if (!Element.isElement(node)) {
    return false;
  }

  return (
    node.type === PARAGRAPH_PLUGIN_ID ||
    (node.type === LIST_PLUGIN_ID &&
      (node.variant === LIST_VARIANTS.ol || node.variant === LIST_VARIANTS.ul))
  );
};

export const isListElement = (node: Node): node is ListPluginElement => {
  return (
    Element.isElement(node) &&
    node.type === LIST_PLUGIN_ID &&
    (node.variant === LIST_VARIANTS.ol || node.variant === LIST_VARIANTS.ul)
  );
};

export const isListWrapperShortcut = (value: string): value is keyof typeof LIST_WRAPPER => {
  return Object.prototype.hasOwnProperty.call(LIST_WRAPPER, value);
};

/**
 * @param {*} pretty - useful if you want to render the hotkey combination as text
 */
export const getIncreaseIndentHotkey = (pretty: boolean = false): string => {
  const hotkey = isApple() ? INCREASE_INDENT_HOTKEY_MAC : INCREASE_INDENT_HOTKEY_WINDOWS;
  return pretty ? prettifyHotkey(hotkey) : hotkey;
};

export const getDecreaseIndentHotkey = (pretty: boolean = false): string => {
  const hotkey = isApple() ? DECREASE_INDENT_HOTKEY_MAC : DECREASE_INDENT_HOTKEY_WINDOWS;
  return pretty ? prettifyHotkey(hotkey) : hotkey;
};

export const getBulletedListHotkey = (pretty: boolean = false): string => {
  const hotkey = isApple() ? BULLETED_LIST_HOTKEY_MAC : BULLETED_LIST_HOTKEY_WINDOWS;
  return pretty ? prettifyHotkey(hotkey) : hotkey;
};

export const getNumberedListHotkey = (pretty: boolean = false): string => {
  const hotkey = isApple() ? NUMBERED_LIST_HOTKEY_MAC : NUMBERED_LIST_HOTKEY_WINDOWS;
  return pretty ? prettifyHotkey(hotkey) : hotkey;
};

export const findClosestBlockPathAtSelection = (editor: Editor, selection?: Range): Path => {
  const block = Editor.above(editor, {
    at: selection,
    match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
  });

  return block ? block[1] : [];
};

export const getCurrentListAtPath = (editor: Editor, path: Path) => {
  return Editor.above(editor, {
    at: path,
    match: (n) => Element.isElement(n) && n.type === LIST_PLUGIN_ID,
  });
};

export const isInsideFirstListItemOfListAtPath = (editor: Editor, path: Path): boolean => {
  const currentList = getCurrentListAtPath(editor, path);

  if (currentList == null) return false;

  const firstListItemPath = [...currentList[1], 0];
  return Path.equals(path, firstListItemPath);
};

export const isInsideListAtPath = (editor: Editor, path: Path): boolean => {
  return !!getCurrentListAtPath(editor, path);
};

export const isInsideListAtSelection = (editor: Editor): boolean => {
  if (editor == null || editor.selection == null) return false;
  const { selection } = editor;
  const { anchor, focus } = selection;

  // find a matching list node in the ancestors
  const findAncestorList = (path: Path) => {
    for (const [node, currentPath] of Array.from(Node.ancestors(editor, path))) {
      if (Element.isElement(node) && node.type === LIST_PLUGIN_ID) {
        return currentPath;
      }
    }
    return null;
  };

  const anchorListPath = findAncestorList(anchor.path);

  if (Range.isCollapsed(selection)) {
    return !!anchorListPath;
  }

  const focusListPath = findAncestorList(focus.path);

  return !!anchorListPath || !!focusListPath;
};

export const getListDepth = (editor: Editor, path: Path) => {
  return Array.from(
    Editor.levels(editor, {
      at: path,
      match: (n) => Element.isElement(n) && n.type === LIST_PLUGIN_ID,
    })
  ).length;
};

/**
 * returns list variant based on the nearest list to the user's selection
 * i.e if selection is 1 list level deep, use top level list variant
 * if selection is 2 list levels deep, use the corresponding sublist variant
 */
export const getCurrentActiveListVariant = (
  editor?: Editor | null,
  range?: Range
): CurrentActiveListVariantState => {
  const selection = range ?? editor?.selection;
  if (editor == null || selection == null) return null;

  const nodeEntry = Editor.above(editor, {
    at: Range.start(selection),
    match: (node) =>
      Element.isElement(node) &&
      node.type === LIST_PLUGIN_ID &&
      (node.variant === LIST_VARIANTS.ol || node.variant === LIST_VARIANTS.ul),
  });
  if (nodeEntry != null) {
    return (nodeEntry[0] as ListPluginElement).variant;
  }
  return null;
};

const isListItemElement = (node: Node): boolean => {
  return (
    Element.isElement(node) &&
    (node.type === LIST_PLUGIN_ID || node.type === PARAGRAPH_PLUGIN_ID) &&
    node.variant === 'li'
  );
};

/**
 * returns the index of the current list item in the list at the current level
 */
export const getCurrentListItemPosition = (
  editor?: Editor | null,
  range?: Range | null
): number => {
  if (editor == null || range == null) return -1;
  try {
    const start = Editor.start(editor, range);
    const startOfSelection = Editor.nodes(editor, {
      at: start,
      match: (node) => isListItemElement(node),
    });
    const { value: firstListItem } = startOfSelection.next();

    if (firstListItem == null) return -1;
    // get selected list item(s)
    const listItems = Editor.nodes(editor, {
      match: (node) => isListItemElement(node),
    });

    if (listItems == null) return -1;

    const { value: currentSelectedItem } = listItems.next();
    if (currentSelectedItem == null) return -1;

    // get parent list
    const parentListEntry = Editor.above(editor, {
      at: currentSelectedItem[1],
      match: (node) =>
        Element.isElement(node) &&
        node.type === LIST_PLUGIN_ID &&
        (node.variant === 'ol' || node.variant === 'ul'),
    });
    // get all list items starting at the parent (includes the current one)
    const allListItemsFromParent = Editor.nodes(editor, {
      match: (n) => isListItemElement(n),
      mode: 'all',
      at: parentListEntry ? parentListEntry[1] : [],
    });
    if (allListItemsFromParent == null) return -1;

    let index = 0;
    for (const [, path] of Array.from(allListItemsFromParent)) {
      if (path.length === currentSelectedItem[1].length) {
        if (Path.equals(path, currentSelectedItem[1])) {
          return index;
        }
        index++;
      }
    }
    return -1;
  } catch (e: any) {
    return -1;
  }
};

export const listItemContainsOnlyEmptyInlineBookmark = (node: Node): boolean => {
  if (
    Element.isElement(node) &&
    ((node.type === LIST_PLUGIN_ID && node.variant !== LIST_VARIANTS.li) ||
      node.type !== PARAGRAPH_PLUGIN_ID)
  ) {
    const variantErrorMessage =
      '[list/utils] listItemContainsOnlyEmptyInlineBookmark was called, but node is not a list item variant';
    logger.warn(variantErrorMessage, {
      node,
    });

    return false;
  }

  if ((node as CustomElement)?.children == null) {
    return false;
  }

  // length of three: inline bookmark node, plus two empty text nodes each side
  if (
    ((node as CustomElement).children.length as number) === 3 &&
    ((node as CustomElement).children[1] as CustomElement).type === INLINE_BOOKMARK_PLUGIN_ID
  ) {
    if (Node.string((node as CustomElement).children[1]) === '') {
      return true;
    }
  }

  return false;
};

export const isListItemEmpty = (node: Node): boolean => {
  if (node.variant !== LIST_VARIANTS.li || node.type !== PARAGRAPH_PLUGIN_ID) {
    const variantErrorMessage =
      '[list/utils] isListItemEmpty was called, but node is not a list item variant';
    logger.warn(variantErrorMessage, {
      node,
    });

    return false;
  }

  if (Node.string(node) === '') {
    return true;
  }

  return false;
};

export const createListItemNode = (text: string = ''): ParagraphPluginElement => ({
  type: PARAGRAPH_PLUGIN_ID,
  variant: LIST_VARIANTS.li,
  children: [{ text: '' }, { ...createInlineBookmark(text) }, { text: '' }],
});

export const createListItemNodeWithChildren = (children: Array<Node>): ParagraphPluginElement => ({
  type: PARAGRAPH_PLUGIN_ID,
  variant: LIST_VARIANTS.li,
  children,
});

const createListNode = (variant: ListVariant, listItemNodes: Array<ParagraphPluginElement>) => ({
  type: LIST_PLUGIN_ID,
  variant,
  children: listItemNodes,
});

/**
 *
 * If the user types a list insertion shortcut followed by a space,
 * then replace their text with a list and the first list item
 *
 * E.g. '1. ' would replace this text with a numbered list, with 1. being the first list item
 */
export const triggerListInsertion = ({
  editor,
  variant,
  at,
  listItemNodes,
  edge = 'end',
}: {
  editor: Editor;
  variant: ListVariant;
  at: Location;
  listItemNodes: Array<ParagraphPluginElement>;
  edge?: 'start' | 'end' | 'all';
}): void => {
  Transforms.insertNodes(editor, [createListNode(variant, listItemNodes)], {
    at,
    select: true,
  });

  if (editor.selection != null) {
    const listItemGenerator = Editor.nodes(editor, {
      at: editor.selection,
      match: (n: Node) => n.type === PARAGRAPH_PLUGIN_ID && n.variant === LIST_VARIANTS.li,
      mode: 'lowest',
    });
    const lowestListItem = listItemGenerator.next().value;

    if (lowestListItem != null) {
      const inlineBookmarkGenerator = Editor.nodes(editor, {
        at: lowestListItem[1],
        match: (n: Node) => n.type === INLINE_BOOKMARK_PLUGIN_ID,
        mode: 'highest',
      });
      const inlineBookmark = inlineBookmarkGenerator.next().value;

      if (inlineBookmark != null) {
        const pathToInlineBookmark = inlineBookmark[1];
        if (edge == null || edge === 'end') {
          Transforms.select(editor, Editor.end(editor, pathToInlineBookmark));
        } else if (edge === 'start') {
          Transforms.select(editor, Editor.start(editor, pathToInlineBookmark));
        } else if (edge === 'all') {
          Transforms.select(editor, pathToInlineBookmark);
        }
      }
    }
  }
};

/**
 * When increasing the indent of a list item, we want to move it inside the previous list item or
 * wrap in a new list
 */
export const increaseListIndentation = (
  editor: Editor,
  variant: ListVariant,
  at: Location
): void => {
  const list = {
    type: 'list',
    variant,
    children: [],
  } as ListPluginElement;
  const pathRefs = Array.from(
    Editor.nodes(editor, {
      at: editor.selection ?? at,
      match: (n) =>
        Element.isElement(n) &&
        n.type === PARAGRAPH_PLUGIN_ID &&
        n.variant === PARAGRAPH_LIST_ITEM_VARIANT,
    })
  ).map((entry) => Editor.pathRef(editor, entry[1]));

  // Iterate through each list item and nest it accordingly
  for (const pathRef of pathRefs) {
    const currLiPath = getPathRefSafe(pathRef);
    const prevNode = Editor.previous(editor, { at: currLiPath });
    const nextNode = Editor.next(editor, { at: currLiPath });
    const prevLiPathRef = prevNode ? Editor.pathRef(editor, prevNode[1]) : undefined;
    const nextListPathRef = nextNode ? Editor.pathRef(editor, nextNode[1]) : undefined;
    const hasPrevList =
      prevNode !== undefined &&
      getListDepth(editor, prevNode[1]) === getListDepth(editor, currLiPath) + 1;
    const hasNextList =
      nextNode !== undefined &&
      getListDepth(editor, nextNode[1]) === getListDepth(editor, currLiPath) + 1;
    /**
     * Test cases: also check depths are the same
     * 1. Indent should nest the selection into the list above
     * 2. Indent should nest the selection into the list below
     * 3. Indent should wrap the selection with the list above and below into a one list
     * 4. Indent should wrap the selection with the list into a new list
     */

    if (hasPrevList && hasNextList) {
      if (
        nextNode === undefined ||
        nextListPathRef === undefined ||
        prevNode === undefined ||
        prevLiPathRef === undefined
      )
        return;
      const prevLiPath = getPathRefSafe(prevLiPathRef);

      Transforms.moveNodes(editor, {
        at: currLiPath,
        to: [...nextNode[1], 0],
      });

      let nextListPath = getPathRefSafe(nextListPathRef);

      // Move the current selection into the previous list
      Transforms.moveNodes(editor, {
        at: nextListPath,
        to: [...prevLiPath, (prevNode[0] as CustomElement).children.length],
      });

      nextListPath = getPathRefSafe(nextListPathRef);

      Transforms.unwrapNodes(editor, { at: nextListPath });
    } else if (hasPrevList) {
      if (prevNode === undefined || prevLiPathRef === undefined) return;
      const prevLiPath = getPathRefSafe(prevLiPathRef);

      // Move the current selection into the previous list
      Transforms.moveNodes(editor, {
        at: currLiPath,
        to: [...prevLiPath, (prevNode[0] as CustomElement).children.length],
        mode: 'lowest',
      });
    } else if (hasNextList) {
      if (nextNode === undefined) return;
      // merge next list if it is the same depth
      Transforms.moveNodes(editor, {
        at: currLiPath,
        to: [...nextNode[1], 0],
      });
    } else {
      Transforms.wrapNodes(editor, list as CustomElement, { at: currLiPath });
    }
  }
};

export const decreaseListIndentation = (
  editor: Editor,
  variant: ListVariant,
  at: Location
): void => {
  const pathRefs = Array.from(
    Editor.nodes(editor, {
      at: editor.selection ?? at,
      match: (n) =>
        Element.isElement(n) &&
        n.type === PARAGRAPH_PLUGIN_ID &&
        n.variant === PARAGRAPH_LIST_ITEM_VARIANT,
    })
  ).map((entry) => Editor.pathRef(editor, entry[1]));

  if (pathRefs.length === 0 && Path.isPath(at) && isInsideListAtPath(editor, at)) {
    const node = Node.get(editor, at);
    if (
      Element.isElement(node) &&
      node.type === PARAGRAPH_PLUGIN_ID &&
      node.variant !== PARAGRAPH_LIST_ITEM_VARIANT
    ) {
      Transforms.liftNodes(editor, { at });
      removeListItemOrphans(editor);
    }
    return;
  }

  // Iterate through each list item and nest it accordingly
  for (const pathRef of pathRefs) {
    const currLiPath = getPathRefSafe(pathRef);
    if (isInsideListAtPath(editor, currLiPath)) {
      Transforms.liftNodes(editor, { at: currLiPath });
    }
    // used when handling case for multi-block selection at lowest list depths
    else if (isInsideListAtSelection(editor)) {
      demoteListItems(editor);
    }
    removeListItemOrphans(editor);
  }
};

export const downgradeOrphanListItemVariantAtNode = (
  editor: Editor,
  node: Node,
  path: Path
): boolean => {
  // Step 1: Only paragraph list items are candidates for downgrade.
  // Ignore all other node types or the editor root path.
  if (
    !Element.isElement(node) ||
    node.type !== PARAGRAPH_PLUGIN_ID ||
    node.variant !== PARAGRAPH_LIST_ITEM_VARIANT ||
    path.length === 0
  ) {
    return false;
  }

  // Step 2: Inspect the direct parent.
  // A list item variant is valid only when its parent is a list node.
  const parent = Node.get(editor, Path.parent(path));
  if (Element.isElement(parent) && parent.type === LIST_PLUGIN_ID) {
    return false;
  }

  // Step 3: Parent is not a list, so this is an orphaned list-item paragraph.
  // Remove the list variant and keep the node as a regular paragraph.
  Transforms.unsetNodes(editor, 'variant', { at: path });
  return true;
};

/**
 *
 * Iterate through the editor, and remove the paragraph list variant,
 * If it's parent is not a list node
 */
export const removeListItemOrphans = (editor: Editor) => {
  for (const [node, path] of Array.from(Node.nodes(editor))) {
    downgradeOrphanListItemVariantAtNode(editor, node, path);
  }
};

/**
 *
 * Iterate through the nodes in a range, and return the list variants within that range
 */
export const getListVariantsInRange = (editor: Editor, at: Range): Array<string> => {
  const variants = new Set<string>();
  for (const [node] of Array.from(Editor.nodes(editor, { at }))) {
    if (!Element.isElement(node)) {
      continue;
    }

    if (node.type === LIST_PLUGIN_ID) {
      const listNode = node as ListPluginElement;
      if (listNode.variant != null) {
        variants.add(String(listNode.variant));
      }
    } else if (node.type === PARAGRAPH_PLUGIN_ID) {
      const paragraphNode = node as ParagraphPluginElement;
      if (paragraphNode.variant == null) {
        variants.add('none');
      }
    } else if (node.type === HEADING_PLUGIN_ID) {
      variants.add('none');
    }
  }

  return Array.from(variants);
};

/**
 *
 * Iterate through the editor, and if we come across an empty list, remove it altogether.
 */
export const removeEmptyLists = (editor: Editor) => {
  for (const [node, path] of Array.from(Node.nodes(editor)).reverse()) {
    if (Element.isElement(node) && node.type === LIST_PLUGIN_ID && Array.isArray(node.children)) {
      const nodeChildren: Array<Node> = node.children;
      if (nodeChildren.length === 0) {
        Transforms.removeNodes(editor, { at: path });
      } else {
        const allChildrenEmpty = nodeChildren.every((child) => Node.string(child).trim() === '');

        if (allChildrenEmpty) {
          const hasBracketDescendant = Array.from(Node.descendants(node)).some(([child]) => {
            return Element.isElement(child) && BRACKET_TYPES.includes(child.type);
          });

          if (hasBracketDescendant) {
            continue;
          }

          Transforms.removeNodes(editor, { at: path });
          removeEmptyLists(editor);
        }
      }
    }
  }
};
